import React, { useEffect, useState } from "react";
import { usePageMetadata } from "../context/PageMetadataContext";
import { Table, Spinner, Badge, Row, Col, Card as BootstrapCard } from "react-bootstrap";
import { Card, CardContent } from "../components/Card";
import { useLanguage } from "../context/LanguageContext";
import { FiDollarSign, FiTag, FiCreditCard, FiTrendingUp } from "react-icons/fi";
import AccountLabel from "../components/AccountLabel";

interface DomainAuctionData {
  amount_10_to_100: number;
  amount_100_to_1000: number;
  amount_1000_to_10000: number;
  amount_above_10000: number;
  highest_bid: number;
  total_amount: number;
  total_claims: number;
}

interface DomainAuctionResponse {
  data: DomainAuctionData;
  generated_at: string;
}

interface ChartDataItem {
  name: string;
  range: string;
  value: number;
  percentage: number;
  fill: string;
}

// Add this interface for top bid data
interface TopBidItem {
  account: string;
  bid_amount: number;
}

const DomainAuctionStats: React.FC = () => {
  const { language, t } = useLanguage();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [auctionData, setAuctionData] = useState<DomainAuctionData | null>(null);
  const [chartData, setChartData] = useState<ChartDataItem[]>([]);
  const [lastUpdated, setLastUpdated] = useState<string>("");
  const { setHeading, setTitle, setDescription } = usePageMetadata();
  const [piPrice, setPiPrice] = useState<number>(2.0); // Default value before loading
  const [topBids, setTopBids] = useState<TopBidItem[]>([]);

  // Colors for chart segments (still used for table colors)
  const COLORS = ['#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  useEffect(() => {
    // Set page metadata
    setHeading(t('domain_auction_stats.heading'));
    setTitle(t('domain_auction_stats.title'));
    setDescription(t('domain_auction_stats.description'));
  }, [setHeading, setTitle, setDescription, language, t]);

  useEffect(() => {
    fetchAuctionData();
    fetchPiPrice();
    fetchTopBids(); // Add this line
  }, [t]);

  const fetchAuctionData = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://api.piscan.io/data/domainauction');
      
      if (response.ok) {
        const data: DomainAuctionResponse = await response.json();
        setAuctionData(data.data);
        setLastUpdated(data.generated_at);
        
        // Process data for the table - exclude the <10 Pi range
        
        const processedChartData: ChartDataItem[] = [
          // Removed the 0-10 Pi range as requested
          {
            name: t('domain_auction_stats.ranges.medium'),
            range: "10-100 π",
            value: data.data.amount_10_to_100,
            // Recalculated percentage based on total excluding the 0-10 range
            percentage: (data.data.amount_10_to_100 / data.data.total_claims) * 100,
            fill: COLORS[0]
          },
          {
            name: t('domain_auction_stats.ranges.high'),
            range: "100-1,000 π",
            value: data.data.amount_100_to_1000,
            percentage: (data.data.amount_100_to_1000 / data.data.total_claims) * 100,
            fill: COLORS[1]
          },
          {
            name: t('domain_auction_stats.ranges.premium'),
            range: "1,000-10,000 π",
            value: data.data.amount_1000_to_10000,
            percentage: (data.data.amount_1000_to_10000 / data.data.total_claims) * 100,
            fill: COLORS[2]
          },
          {
            name: t('domain_auction_stats.ranges.elite'),
            range: ">10,000 π",
            value: data.data.amount_above_10000,
            percentage: (data.data.amount_above_10000 / data.data.total_claims) * 100,
            fill: COLORS[3]
          }
        ];
        
        setChartData(processedChartData);
      } else {
        setError("Failed to fetch auction data");
      }
    } catch (err) {
      console.error("Error fetching domain auction data:", err);
      setError("Error loading data");
    } finally {
      setLoading(false);
    }
  };

  const fetchPiPrice = async () => {
    try {
      // Fetch from OKX API
      const response = await fetch('https://api.piscan.io/data/pi-price');
      if (response.ok) {
        const data = await response.json();
        if (data && data.data && data.data[0]) {
          setPiPrice(parseFloat(data.data[0].idxPx));
        }
      }
    } catch (error) {
      console.error("Failed to fetch Pi price:", error);
      // Keep using default price if fetch fails
    }
  };

  // Update the fetchTopBids function to properly handle the correct response format

  const fetchTopBids = async () => {
    try {
      const response = await fetch('https://api.piscan.io/data/topbid');
      
      if (response.ok) {
        const responseData = await response.json();
        // The data is already in the correct format with account and bid_amount properties
        setTopBids(responseData.data);
        // You could also use the generated_at timestamp if needed
        // setLastUpdatedTopBids(responseData.generated_at);
      }
    } catch (error) {
      console.error("Error fetching top bids:", error);
    }
  };

  // Format large numbers with abbreviations (K, M, B)
  const formatLargeNumber = (value: number): string => {
    // Use translation keys for abbreviations
    if (value >= 1_000_000_000) {
      return (value / 1_000_000_000).toFixed(1) + t('number.billion_short');
    } else if (value >= 1_000_000) {
      return (value / 1_000_000).toFixed(1) + t('number.million_short');
    } else if (value >= 1_000) {
      return (value / 1_000).toFixed(1) + t('number.thousand_short');
    } else {
      return value.toLocaleString(language);
    }
  };

  // Format Pi value (from strath to Pi)
  const formatPiAmount = (strathValue: number): string => {
    return (strathValue / 10_000_000).toLocaleString(language, { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    });
  };

  // Format USD value
  const formatUsdAmount = (piAmount: number): string => {
    const usdValue = piAmount * piPrice;
    return usdValue.toLocaleString(language, { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    });
  };

  // Format percentage
  const formatPercentage = (value: number): string => {
    return value.toFixed(2);
  };

  // Format date
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString(language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{t('common.loading')}</span>
        </Spinner>
      </div>
    );
  }

  if (error || !auctionData) {
    return (
      <div className="alert alert-danger" role="alert">
        {error || t('common.error_loading_data')}
      </div>
    );
  }

  return (
    <div className="domain-auction-stats-page">
      <div className="mb-4">
        <p className="text-muted">
          {t('domain_auction_stats.intro_text')}
        </p>
        <p className="small text-muted">
          {t('domain_auction_stats.last_updated')}: {formatDate(lastUpdated)}
        </p>
      </div>

      {/* Metrics Cards in Nodes style */}
      <Row className="mb-4">
        <Col md={3} sm={6} className="mb-4 mb-md-0">
          <BootstrapCard className="h-100 shadow-sm">
            <BootstrapCard.Body className="d-flex align-items-center">
              <div className="rounded-circle p-3 bg-primary bg-opacity-10 me-3">
                {FiTag({size:24})}
              </div>
              <div>
                <h6 className="text-muted mb-1">{t('domain_auction_stats.total_bids')}</h6>
                <h3 className="mb-0 stats-number">{auctionData.total_claims.toLocaleString(language)}</h3>
              </div>
            </BootstrapCard.Body>
          </BootstrapCard>
        </Col>
        
        <Col md={3} sm={6} className="mb-4 mb-md-0">
          <BootstrapCard className="h-100 shadow-sm">
            <BootstrapCard.Body className="d-flex align-items-center">
              <div className="rounded-circle p-3 bg-success bg-opacity-10 me-3">
                {FiCreditCard({size:24,className:"text-success"})}
              </div>
              <div>
                <h6 className="text-muted mb-1">{t('domain_auction_stats.total_pi_bid')}</h6>
                <h3 className="mb-0 stats-number">{formatPiAmount(auctionData.total_amount)} π</h3>
              </div>
            </BootstrapCard.Body>
          </BootstrapCard>
        </Col>
        
        <Col md={3} sm={6} className="mb-4 mb-md-0">
          <BootstrapCard className="h-100 shadow-sm">
            <BootstrapCard.Body className="d-flex align-items-center">
              <div className="rounded-circle p-3 bg-info bg-opacity-10 me-3">
                {FiDollarSign({size:24,className:"text-info"})}
              </div>
              <div>
                <h6 className="text-muted mb-1">{t('domain_auction_stats.estimated_value')}</h6>
                <h3 className="mb-0 stats-number">${formatUsdAmount(auctionData.total_amount / 10_000_000)}</h3>
              </div>
            </BootstrapCard.Body>
          </BootstrapCard>
        </Col>
        
        <Col md={3} sm={6}>
          <BootstrapCard className="h-100 shadow-sm">
            <BootstrapCard.Body className="d-flex align-items-center">
              <div className="rounded-circle p-3 bg-warning bg-opacity-10 me-3">
                {FiTrendingUp({size:24,className:"text-warning"})}
              </div>
              <div>
                <h6 className="text-muted mb-1">{t('domain_auction_stats.highest_bid')}</h6>
                <h3 className="mb-0 stats-number">{formatPiAmount(auctionData.highest_bid)} π</h3>
              </div>
            </BootstrapCard.Body>
          </BootstrapCard>
        </Col>
      </Row>

      {/* Detailed Table */}
      <Card className="none-radius">
        <CardContent>
          <h5 className="card-title">{t('domain_auction_stats.details_title')}</h5>
          <div className="table-responsive">
            <Table hover className="mt-3 mb-0">
              <thead>
                <tr>
                  <th>{t('domain_auction_stats.table.price_range')}</th>
                  <th className="text-end">{t('domain_auction_stats.table.bid_count')}</th>
                  <th className="text-end">{t('domain_auction_stats.table.percentage')}</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <span className="fw-bold" style={{ color: item.fill }}>
                        {item.name}
                      </span>
                      <span className="text-muted ms-2">({item.range})</span>
                    </td>
                    <td className="text-end">{item.value.toLocaleString(language)}</td>
                    <td className="text-end">
                      <Badge bg="light" text="dark">
                        {formatPercentage(item.percentage)}%
                      </Badge>
                    </td>
                  </tr>
                ))}
                <tr className="fw-bold" style={{ backgroundColor: 'rgba(0,0,0,0.03)' }}>
                  <td>{t('domain_auction_stats.table.total')}</td>
                  <td className="text-end">
                    {(auctionData.total_claims ).toLocaleString(language)}
                  </td>
                  <td className="text-end">
                    <Badge bg="primary">100%</Badge>
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        </CardContent>
      </Card>
      <div className="row g-3 mb-4 d-block d-md-none">
        {/* <AdSenseUnit/> */}
        {/* <AdUnit
                  className="67cc9f95aa72d3d47fe128c2"
                  width="300px"
                  height="100px"
                  centered={true}
                /> */}
      </div>
      {/* Top Bids Table - new table */}
      <Card className="none-radius mt-4">
        <CardContent>
          <h5 className="card-title">{t('domain_auction_stats.top_bids_title') || "Top Bids"}</h5>
          <div className="table-responsive">
            <Table hover className="mt-3 mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('domain_auction_stats.table.account') || "Account"}</th>
                  <th className="text-end">{t('domain_auction_stats.table.bid_amount') || "Bid Amount"}</th>
                  <th className="text-end">{t('domain_auction_stats.table.usd_value') || "USD Value"}</th>
                </tr>
              </thead>
              <tbody>
                {topBids.map((item, index) => (
                  <tr key={index}>
                    <td className="text-muted">{index + 1}</td>
                    <td>
                      <AccountLabel account={item.account} shorten={true} />
                    </td>
                    <td className="text-end fw-medium">
                      {formatPiAmount(item.bid_amount)} π
                    </td>
                    <td className="text-end text-muted">
                      ${formatUsdAmount(item.bid_amount / 10_000_000)}
                    </td>
                  </tr>
                ))}
                {topBids.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center py-4 text-muted">
                      {loading ? "Loading..." : "No top bid data available"}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DomainAuctionStats;